import { useParams, useNavigate } from 'react-router-dom';
import { usePermission } from '@/hooks/usePermission';
import { useState } from 'react';
import { Tabs, Tab, Button, Spinner, Avatar, useDisclosure } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { StatusChip } from '@/components/common/StatusChip';
import { UsageGauge } from '@/components/common/UsageGauge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { MailboxFormModal } from '@/features/mailboxes/components/MailboxFormModal';
import { MailboxResetPasswordModal } from '@/features/mailboxes/components/MailboxResetPasswordModal';
import { useMailbox, useDeleteMailbox, useUpdateMailbox } from '@/features/mailboxes/hooks';
import { formatBytes, formatRelative } from '@/lib/format';
import { PencilSquareIcon, KeyIcon, PauseCircleIcon, PlayCircleIcon, TrashIcon, ArrowDownTrayIcon } from '@/icons';

export function MailboxDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: mb, isLoading, isError } = useMailbox(id);
  const editModal = useDisclosure();
  const resetModal = useDisclosure();
  const del = useDeleteMailbox();
  const canManage = usePermission().can('mailbox:manage');
  const update = useUpdateMailbox(id);
  const [confirm, setConfirm] = useState<null | 'delete' | 'suspend' | 'activate'>(null);

  if (isLoading) return <div className="flex justify-center py-20"><Spinner label="Loading mailbox…" /></div>;
  if (isError || !mb) return <div className="py-20 text-center"><p className="text-fg-muted">Mailbox not found.</p><Button className="mt-4" variant="flat" onPress={() => navigate('/mailboxes')}>Back</Button></div>;

  const suspended = mb.status === 'suspended';
  const pct = mb.quotaBytes > 0 ? (mb.usedBytes / mb.quotaBytes) * 100 : 0;

  const runConfirm = async () => {
    if (confirm === 'delete') { await del.mutateAsync(mb.id); navigate('/mailboxes'); }
    else if (confirm) await update.mutateAsync({ status: confirm === 'suspend' ? 'suspended' : 'active' });
    setConfirm(null);
  };

  return (
    <div>
      <PageHeader title={mb.displayName} actions={
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="flat" startContent={<PencilSquareIcon className="h-4 w-4" />} onPress={editModal.onOpen}>Edit</Button>
          <Button size="sm" variant="flat" startContent={<KeyIcon className="h-4 w-4" />} onPress={resetModal.onOpen}>Reset password</Button>
          {canManage && <Button size="sm" variant="flat" startContent={<ArrowDownTrayIcon className="h-4 w-4" />} onPress={() => navigate(`/migrations?mailbox=${encodeURIComponent(mb.id)}`)}>Migrate mail</Button>}
          <Button size="sm" variant="flat" startContent={suspended ? <PlayCircleIcon className="h-4 w-4" /> : <PauseCircleIcon className="h-4 w-4" />} onPress={() => setConfirm(suspended ? 'activate' : 'suspend')}>{suspended ? 'Re-activate' : 'Suspend'}</Button>
          {canManage && <Button size="sm" color="danger" variant="flat" startContent={<TrashIcon className="h-4 w-4" />} onPress={() => setConfirm('delete')}>Delete</Button>}
        </div>
      } />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-surface p-6">
          <div className="flex flex-col items-center text-center">
            <Avatar size="lg" name={(mb.displayName || mb.address).slice(0, 2).toUpperCase()} className="h-16 w-16 bg-brand/15 text-brand" />
            <h2 className="mt-3 text-lg font-semibold text-fg">{mb.displayName}</h2>
            <p className="text-sm text-fg-muted">{mb.address}</p>
            <div className="mt-3">{suspended ? <StatusChip tone="danger">Suspended</StatusChip> : <StatusChip tone="success">Active</StatusChip>}</div>
          </div>
          <dl className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-fg-muted">Created</dt><dd className="text-fg">{formatRelative(mb.createdAt)}</dd></div>
            <div className="flex justify-between"><dt className="text-fg-muted">Quota</dt><dd className="text-fg">{formatBytes(mb.quotaBytes)}</dd></div>
            <div className="flex justify-between"><dt className="text-fg-muted">Used</dt><dd className="text-fg">{formatBytes(mb.usedBytes)}</dd></div>
          </dl>
        </div>
        <div className="lg:col-span-2">
          <Tabs aria-label="Mailbox details" variant="underlined" color="primary">
            <Tab key="quota" title="Quota & usage">
              <div className="rounded-xl border border-border bg-surface p-6">
                <div className="flex flex-col items-center sm:flex-row sm:gap-8">
                  <UsageGauge percent={pct} label="Storage used" caption={`${formatBytes(mb.usedBytes)} of ${formatBytes(mb.quotaBytes)}`} />
                  <div className="mt-4 flex-1 sm:mt-0">
                    <p className="text-sm text-fg-muted">This mailbox is using <span className="font-medium text-fg">{Math.round(pct)}%</span> of its allocated storage.</p>
                    <Button className="mt-4" size="sm" variant="flat" startContent={<PencilSquareIcon className="h-4 w-4" />} onPress={editModal.onOpen}>Adjust quota</Button>
                  </div>
                </div>
              </div>
            </Tab>
            <Tab key="security" title="Security">
              <div className="rounded-xl border border-border bg-surface p-6">
                <p className="text-sm font-medium text-fg">Password</p>
                <p className="mt-1 text-sm text-fg-muted">Reset the mailbox password or force a re-authentication.</p>
                <Button className="mt-4" size="sm" variant="flat" startContent={<KeyIcon className="h-4 w-4" />} onPress={resetModal.onOpen}>Reset password</Button>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      <MailboxFormModal isOpen={editModal.isOpen} onClose={editModal.onClose} mailbox={mb} />
      <MailboxResetPasswordModal isOpen={resetModal.isOpen} onClose={resetModal.onClose} mailbox={mb} />
      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={runConfirm}
        title={confirm === 'delete' ? 'Delete mailbox?' : confirm === 'suspend' ? 'Suspend mailbox?' : 'Re-activate mailbox?'}
        body={confirm === 'delete' ? `Permanently delete ${mb.address}? This cannot be undone.` : confirm === 'suspend' ? `${mb.address} will be unable to send or receive mail.` : `Restore mail access for ${mb.address}.`}
        confirmLabel={confirm === 'delete' ? 'Delete' : confirm === 'suspend' ? 'Suspend' : 'Activate'}
        tone={confirm === 'delete' ? 'danger' : confirm === 'suspend' ? 'warning' : 'primary'} isLoading={del.isPending || update.isPending} />
    </div>
  );
}
